import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Radio,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { SeriesChart, seriesColor } from '../../components/charts/SeriesChart';
import {
  OVER_TIME_FIELDS,
  chartSeriesOf,
  seriesLabel,
  tableRowsOf,
  yRangeOf,
  type OverTimeData,
  type TableRow,
} from './overTimeModel';

/**
 * E34 · WorksheetEntriesOverTime — "Client Hub — Worksheet over time" (cc-homework, chapter "Worksheets —
 * Responses & documents (portal, E34)", buildOrder 67, D-entries-over-time-2026-10-07). A repeated worksheet's
 * numbers: one line per number / scale field (and a score line where the template scores), each point an entry,
 * a Chart / Table toggle over the same read model, the latest value and the entry count under the chart, and
 * Open on each table row (the WorksheetResponse). Text answers are never charted — they stay in the entries.
 *
 * Behavior lines implemented here:
 *   · filter: chart · table (toggle, same read model);
 *   · empty-state: no submission yet → "Nothing to draw yet" (worksheets.overTime.empty);
 *   · format: x axis = submission date, y axis = the field's own scale, one colour per field;
 *   · nav: Open on a table row → the response.
 * No verdict, no threshold, no alert. Reads `worksheetEntriesOverTime(subject, templateId)` — null for a
 * worksheet that is not the caller's to see.
 */
export const WorksheetOverTimePage = () => {
  const t = useTranslate();
  const navigate = useNavigate();
  const clientName = (useLocation().state as { clientName?: string } | null)?.clientName;
  const { i18n } = useTranslation();
  const { subject, templateId } = useParams<{
    subject: string;
    templateId: string;
  }>();
  const lang: 'en' | 'ru' = i18n.languages[0]?.startsWith('ru') ? 'ru' : 'en';
  const locale = lang === 'ru' ? ru : enUS;
  const [view, setView] = useState<'chart' | 'table'>('chart');
  const when = (iso: string) =>
    format(parseISO(iso), 'd MMM HH:mm', { locale });
  const day = (iso: string) => format(parseISO(iso), 'd MMM', { locale });

  const { data, isFetching, isError } = useCustom<OverTimeData>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'worksheetEntriesOverTime',
      fields: OVER_TIME_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: subject },
        templateId: { name: 'templateId', type: 'ID!', value: templateId },
      },
    },
    queryOptions: { enabled: !!subject && !!templateId, retry: false },
  });
  const r = data?.data ?? null;
  // the empty state names the sent date: the latest assignment of this template, from the hub's own read model
  const sentRows = useCustom<{ templateId: string; sentAt: string }[]>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientWorksheets',
      fields: ['templateId', 'sentAt'],
      variables: {
        subject: { name: 'subject', type: 'ID!', value: subject },
        filter: { name: 'filter', type: 'String', value: 'all' },
      },
    },
    queryOptions: { enabled: !!subject },
  });
  const sentAt =
    (sentRows.data?.data ?? [])
      .filter((x) => x.templateId === templateId)
      .map((x) => x.sentAt)
      .sort()
      .pop() ?? null;
  const scoreWord = t('worksheets.overTime.score');
  const series = useMemo(
    () => (r ? chartSeriesOf(r, scoreWord) : []),
    [r, scoreWord],
  );
  const rows = useMemo(() => (r ? tableRowsOf(r) : []), [r]);
  const empty = !!r && r.series.length === 0;
  const first = rows.length ? rows[rows.length - 1].occurredAt : null;
  const last = rows.length ? rows[0].occurredAt : null;

  return (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%' }}
      data-testid="worksheet-over-time"
    >
      <Typography.Text type="secondary" style={{ textTransform: 'uppercase', fontSize: 12, letterSpacing: 0.5 }} data-testid="worksheet-over-time-eyebrow">
        {clientName
          ? t('worksheets.overTime.eyebrow', { name: clientName })
          : t('worksheets.overTime.eyebrowAnon')}
      </Typography.Text>
      <Link
        to={`/clients/show/${encodeURIComponent(subject ?? '')}?tab=worksheets`}
        data-testid="worksheet-over-time-back"
      >
        ← {t('worksheets.overTime.back')}
      </Link>

      {isFetching && !r ? <Spin /> : null}
      {isError ? (
        <Alert
          type="error"
          showIcon
          message={t('worksheets.overTime.loadFailed')}
          data-testid="worksheet-over-time-error"
        />
      ) : null}
      {!isFetching && !isError && !r ? (
        <Alert
          type="info"
          showIcon
          message={t('worksheets.overTime.notAvailable')}
          data-testid="worksheet-over-time-missing"
        />
      ) : null}

      {r ? (
        <>
          <Typography.Title
            level={3}
            style={{ margin: 0 }}
            data-testid="worksheet-over-time-title"
          >
            {t('worksheets.overTime.title', { name: r.name ?? r.templateId })}
          </Typography.Title>

          {empty ? (
            <Card data-testid="worksheet-over-time-empty">
              <Typography.Text strong>
                {t('worksheets.overTime.empty')}
              </Typography.Text>
              <br />
              <Typography.Text type="secondary" data-testid="worksheet-over-time-empty-hint">
                {sentAt
                  ? t('worksheets.overTime.emptySent', { date: format(parseISO(sentAt), 'd MMM yyyy', { locale }) })
                  : t('worksheets.overTime.emptyHint')}
              </Typography.Text>
            </Card>
          ) : (
            <>
              <Space
                style={{ width: '100%', justifyContent: 'space-between' }}
                wrap
              >
                <Radio.Group
                  optionType="button"
                  buttonStyle="solid"
                  value={view}
                  onChange={(e) => setView(e.target.value)}
                  data-testid="worksheet-over-time-toggle"
                >
                  <Radio.Button
                    value="chart"
                    data-testid="worksheet-over-time-chart-tab"
                  >
                    {t('worksheets.overTime.chart')}
                  </Radio.Button>
                  <Radio.Button
                    value="table"
                    data-testid="worksheet-over-time-table-tab"
                  >
                    {t('worksheets.overTime.table')}
                  </Radio.Button>
                </Radio.Group>
                <Typography.Text
                  type="secondary"
                  data-testid="worksheet-over-time-summary"
                >
                  {t('worksheets.overTime.summary', {
                    count: r.entryCount,
                    from: first ? day(first) : '',
                    to: last ? day(last) : '',
                  })}
                </Typography.Text>
              </Space>

              {view === 'chart' ? (
                <Card data-testid="worksheet-over-time-chart">
                  <SeriesChart
                    series={series}
                    yRange={yRangeOf(r)}
                    locale={lang}
                    ariaLabel={t('worksheets.overTime.chartLabel', {
                      name: r.name ?? r.templateId,
                    })}
                    testId="worksheet-over-time-series-chart"
                  />
                  <Space
                    direction="vertical"
                    size={4}
                    style={{ marginTop: 12 }}
                  >
                    {r.series.map((s, i) => (
                      <Space
                        key={s.fieldKey}
                        size={8}
                        data-testid={`worksheet-over-time-legend-${s.fieldKey}`}
                      >
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: seriesColor(i),
                          }}
                        />
                        <Typography.Text>
                          {t('worksheets.overTime.legend', {
                            label: seriesLabel(s, scoreWord),
                            latest: s.latest,
                            count: s.count,
                          })}
                        </Typography.Text>
                      </Space>
                    ))}
                  </Space>
                </Card>
              ) : (
                <Card data-testid="worksheet-over-time-table">
                  <Table<TableRow>
                    rowKey="submissionId"
                    size="middle"
                    pagination={false}
                    dataSource={rows}
                    scroll={{ x: 'max-content' }}
                    onRow={(row) =>
                      ({
                        'data-testid': `worksheet-over-time-row-${row.submissionId}`,
                      } as never)
                    }
                    columns={[
                      {
                        title: t('worksheets.overTime.entryColumn'),
                        render: (_, row) => when(row.occurredAt),
                      },
                      ...r.series.map((s) => ({
                        title: seriesLabel(s, scoreWord),
                        key: s.fieldKey,
                        render: (_: unknown, row: TableRow) => (
                          <span
                            data-testid={`worksheet-over-time-cell-${row.submissionId}-${s.fieldKey}`}
                          >
                            {row.values[s.fieldKey] ?? '—'}
                          </span>
                        ),
                      })),
                      {
                        title: '',
                        render: (_, row) => (
                          <Button
                            size="small"
                            type="link"
                            onClick={() =>
                              navigate(
                                `/worksheets/response/${encodeURIComponent(
                                  row.submissionId,
                                )}`,
                              )
                            }
                            data-testid={`worksheet-over-time-open-${row.submissionId}`}
                          >
                            {t('worksheets.overTime.open')}
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Card>
              )}
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('worksheets.overTime.note')}
              </Typography.Text>
            </>
          )}
        </>
      ) : null}
    </Space>
  );
};
